import {
  Users,
  Briefcase,
  CalendarDays,
  Clock,
  DollarSign,
  CheckSquare,
  Bell,
  TrendingUp,
  ChevronUp,
} from 'lucide-react';

const CHAMPAGNE = '#E8B96A';
const CHAMPAGNE_LIGHT = '#F0C674';
const FG = '#F5F1FA';
const MUTED = '#A99FB8';
const SURFACE = '#1B1029';
const SURFACE2 = '#140C20';
const VIOLET = '#6D28D9';
const VIOLET_LIGHT = '#A855F7';

interface WidgetData {
  icon: React.ElementType;
  label: string;
  value: string;
  sub?: string;
  accent?: 'violet' | 'champagne' | 'green';
}

const WIDGETS: WidgetData[] = [
  {
    icon: Users,
    label: 'Clientes',
    value: '128',
    sub: 'Contatos ativos',
    accent: 'violet',
  },
  {
    icon: Briefcase,
    label: 'Processos',
    value: '96',
    sub: 'ativos, 340 no total',
  },
  {
    icon: CalendarDays,
    label: 'Agenda de hoje',
    value: '2',
    sub: 'compromissos',
  },
  {
    icon: Clock,
    label: 'Prazos próximos',
    value: '3',
    sub: 'prazos no radar',
    accent: 'violet',
  },
  {
    icon: DollarSign,
    label: 'Financeiro',
    value: 'R$ 84.500',
    sub: 'Saldo atual',
    accent: 'green',
  },
  {
    icon: CheckSquare,
    label: 'Tarefas',
    value: '5',
    sub: 'pendentes',
  },
  {
    icon: Bell,
    label: 'Intimações',
    value: '3',
    sub: 'não lidas',
    accent: 'champagne',
  },
];

export function DashboardMockup() {
  return (
    <div
      style={{
        width: '100%',
        borderRadius: 16,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.08)',
        background: SURFACE2,
        boxShadow:
          '0 0 0 1px rgba(109,40,217,0.15), 0 32px 80px rgba(0,0,0,0.55), 0 8px 24px rgba(109,40,217,0.12)',
        fontFamily: "'Hanken Grotesk', sans-serif",
      }}
    >
      {/* Toolbar faux */}
      <div
        style={{
          background: 'rgba(20,12,32,0.98)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          padding: '12px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap' as const,
        }}
      >
        {/* Traffic lights */}
        <div style={{ display: 'flex', gap: 7, flexShrink: 0 }}>
          {['#FF5F56', '#FFBD2E', '#27C93F'].map((c) => (
            <span
              key={c}
              style={{
                width: 11,
                height: 11,
                borderRadius: '50%',
                background: c,
                opacity: 0.75,
                display: 'inline-block',
              }}
            />
          ))}
        </div>

        {/* Saudacao */}
        <p
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: FG,
            margin: 0,
            flex: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Boa tarde, Dra. Helena{' '}
          <span style={{ fontWeight: 400, color: MUTED, fontSize: 12 }}>
            Escritório Mendes
          </span>
        </p>

        {/* Segmented control */}
        <div
          style={{
            display: 'flex',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {['Hoje', 'Semana', 'Mês'].map((label, i) => (
            <button
              key={label}
              style={{
                fontSize: 11,
                fontWeight: i === 0 ? 600 : 400,
                color: i === 0 ? FG : MUTED,
                background: i === 0 ? 'rgba(109,40,217,0.4)' : 'transparent',
                border: 'none',
                padding: '5px 12px',
                cursor: 'default',
                fontFamily: 'inherit',
                transition: 'none',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid de widgets */}
      <style>{`
        @media (max-width: 600px) {
          .dash-widgets-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .dash-toolbar { flex-wrap: wrap; gap: 8px !important; }
        }
      `}</style>
      <div
        className="dash-widgets-grid"
        style={{
          padding: '16px 16px 18px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: 10,
        }}
      >
        {WIDGETS.map((w) => (
          <WidgetCard key={w.label} data={w} />
        ))}

        {/* Widget extra: linha de atividade */}
        <ActivityWidget />
      </div>
    </div>
  );
}

function WidgetCard({ data }: { data: WidgetData }) {
  const Icon = data.icon;

  const accentColor =
    data.accent === 'champagne'
      ? CHAMPAGNE
      : data.accent === 'green'
        ? '#4ADE80'
        : data.accent === 'violet'
          ? VIOLET_LIGHT
          : 'rgba(255,255,255,0.28)';

  const accentBg =
    data.accent === 'champagne'
      ? 'rgba(232,185,106,0.08)'
      : data.accent === 'green'
        ? 'rgba(74,222,128,0.06)'
        : data.accent === 'violet'
          ? 'rgba(168,85,247,0.08)'
          : 'rgba(255,255,255,0.03)';

  const borderColor =
    data.accent === 'champagne'
      ? 'rgba(232,185,106,0.22)'
      : data.accent === 'green'
        ? 'rgba(74,222,128,0.18)'
        : data.accent === 'violet'
          ? 'rgba(168,85,247,0.22)'
          : 'rgba(255,255,255,0.07)';

  return (
    <div
      style={{
        background: accentBg,
        border: `1px solid ${borderColor}`,
        borderRadius: 11,
        padding: '14px 14px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Glow decorativo leve */}
      {data.accent && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 80,
            height: 80,
            background: `radial-gradient(ellipse at top right, ${accentColor}18 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Cabecalho: icone + label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 26,
            height: 26,
            borderRadius: 7,
            background: `${accentColor}18`,
            flexShrink: 0,
          }}
        >
          <Icon size={13} color={accentColor} strokeWidth={2} />
        </span>
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 500,
            color: MUTED,
            letterSpacing: '0.02em',
            lineHeight: 1,
          }}
        >
          {data.label}
        </span>
      </div>

      {/* Valor */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: data.value.length > 6 ? 18 : 26,
            fontWeight: 400,
            color: FG,
            lineHeight: 1,
            letterSpacing: '-0.02em',
          }}
        >
          {data.value}
        </span>
        {data.accent === 'green' && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              fontSize: 10,
              fontWeight: 600,
              color: '#4ADE80',
            }}
          >
            <ChevronUp size={10} strokeWidth={2.5} />
            +8%
          </span>
        )}
      </div>

      {/* Sub */}
      {data.sub && (
        <span
          style={{
            fontSize: 10,
            color: 'rgba(169,159,184,0.6)',
            lineHeight: 1.2,
          }}
        >
          {data.sub}
        </span>
      )}
    </div>
  );
}

function ActivityWidget() {
  const items = [
    { label: 'Processo 1027/2024', sub: 'Movimentação registrada', dot: VIOLET_LIGHT },
    { label: 'Helena Mendes', sub: 'Assinatura pendente', dot: CHAMPAGNE },
    { label: 'Prazo fatal amanhã', sub: 'Contestação - 5001/2025', dot: '#F87171' },
  ];

  return (
    <div
      style={{
        gridColumn: '1 / -1',
        background: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 11,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          marginBottom: 10,
        }}
      >
        <TrendingUp size={12} color={VIOLET_LIGHT} strokeWidth={2} />
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            color: MUTED,
            letterSpacing: '0.04em',
            textTransform: 'uppercase' as const,
          }}
        >
          Atividade recente
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {items.map((item, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '7px 0',
              borderBottom:
                i < items.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
            }}
          >
            <span
              style={{
                flexShrink: 0,
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: item.dot,
                boxShadow: `0 0 6px ${item.dot}80`,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  fontSize: 11.5,
                  fontWeight: 500,
                  color: FG,
                  margin: 0,
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.label}
              </p>
              <p
                style={{
                  fontSize: 10.5,
                  color: MUTED,
                  margin: 0,
                  lineHeight: 1.3,
                }}
              >
                {item.sub}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
